import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { Folder, Add } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { FlexAlerts, AlertSeverityTypes } from '../../../components/Message/Alerts/Alerts';
import invoicesService from '../../../services/invoicesService';

const MAX_FILE_SIZE = 52428800;

const fileSizeValidator = (file) => {
  if (file?.size > MAX_FILE_SIZE) {
    return {
      code: 'file-size-too-large',
      message: `File cannot be larger than 50MB.`,
    };
  }
  return null;
};

const listStyle = {
  bgcolor: 'rgba(0, 0, 0, 0.06)',
  borderRadius: '4px',
};

const dropZoneBoxStyle = {
  padding: 5,
  border: 2,
  borderColor: 'rgba(0,0,0,0.12)',
  borderStyle: 'dashed',
  borderRadius: '4px',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
};

const openDropZoneBoxStyle = {
  ...dropZoneBoxStyle,
  padding: 10,
  cursor: 'pointer',
};

const CustomFileDropzone = (props) => {
  const { invoiceId, customInvoiceFiles, handleChangeCustomFiles } = props;
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 10,
    validator: fileSizeValidator,
    onDrop: (files, fileRejections) => handleFilesSelected(files, fileRejections),
  });
  const [messages, setMessages] = useState([]);
  const [isAddNewFilesMode, setIsAddNewFilesMode] = useState(true);
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    setMessages([]);
  }, [isAddNewFilesMode]);

  useEffect(() => {
    // If invoice has custom files, display them by default
    if (customInvoiceFiles?.length > 0) {
      setIsAddNewFilesMode(false);
    }
  }, [customInvoiceFiles]);

  const viewCustomInvoiceFile = (fileName) => {
    setMessages([]);
    if (!fileName) {
      setMessages([
        {
          message: 'Cannot retrieve invoice file without a name.',
          severity: AlertSeverityTypes.error,
        },
      ]);
      return;
    }
    invoicesService.getInvoiceFile(invoiceId, fileName, true).catch((error) => {
      setMessages([
        {
          message: `Cannot retrieve invoice file. ${error?.response?.data?.message}`,
          severity: AlertSeverityTypes.error,
        },
      ]);
    });
  };

  const handleFilesSelected = (acceptedFiles, fileRejections) => {
    if (acceptedFiles?.length > 0) {
      setPendingFiles(acceptedFiles);
      handleChangeCustomFiles(acceptedFiles);
    }
    if (fileRejections?.length > 0) {
      setMessages(
        fileRejections.map((rejection) => ({
          message: rejection?.errors?.[0]?.message ?? 'File was rejected.',
          severity: AlertSeverityTypes.error,
        }))
      );
      return;
    }
    setMessages([]);
  };

  const handleCancelUpload = () => {
    setPendingFiles([]);
    handleChangeCustomFiles([]);
  };

  return (
    <>
      <FlexAlerts messages={messages} />

      {/* Display pending files */}
      {isAddNewFilesMode && pendingFiles?.length > 0 && (
        <Box sx={dropZoneBoxStyle}>
          <Stack spacing={1}>
            <List sx={listStyle}>
              {pendingFiles?.map((file) => {
                return (
                  <ListItem key={file?.name}>
                    <ListItemIcon>
                      <Folder />
                    </ListItemIcon>
                    <ListItemText primary={`${file?.name}`} />
                  </ListItem>
                );
              })}
            </List>
            <Button variant="contained" onClick={() => handleCancelUpload()}>
              Cancel Upload
            </Button>
          </Stack>
        </Box>
      )}

      {/* Display dropzone to add files */}
      {isAddNewFilesMode && pendingFiles?.length < 1 && (
        <Stack spacing={1}>
          <div {...getRootProps()}>
            <Box sx={openDropZoneBoxStyle}>
              <input {...getInputProps()} />
              <Stack justifyItems="center" alignItems="center">
                <Typography variant="h6" sx={{ textAlign: 'center' }}>
                  Drag and drop a custom PDF here. Or click on + to open file explorer
                </Typography>
                <Add sx={{ fontSize: 50 }} />
              </Stack>
            </Box>
          </div>
          {customInvoiceFiles?.length >= 1 && (
            <Button variant="contained" onClick={() => setIsAddNewFilesMode(false)}>
              Cancel
            </Button>
          )}
        </Stack>
      )}

      {/* Display already saved files for invoice */}
      {!isAddNewFilesMode && (
        <Box sx={dropZoneBoxStyle}>
          <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'center' }}>
            Uploaded Custom Invoice Files
          </Typography>
          {customInvoiceFiles?.length === 1 ? (
            <>
              <Typography
                style={{ marginBottom: 30, textAlign: 'center' }}
              >{`${customInvoiceFiles[0]?.path}.pdf`}</Typography>
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Button variant="outlined" onClick={() => viewCustomInvoiceFile(customInvoiceFiles[0]?.path)}>
                  View
                </Button>
                <Button style={{ marginLeft: 10 }} variant="contained" onClick={() => setIsAddNewFilesMode(true)}>
                  Select New Files
                </Button>
              </Box>
            </>
          ) : (
            <Stack spacing={1}>
              <List sx={listStyle}>
                {customInvoiceFiles?.map((file) => {
                  return (
                    <ListItemButton key={file?.path} onClick={() => viewCustomInvoiceFile(file?.path)}>
                      <ListItemIcon>
                        <Folder />
                      </ListItemIcon>
                      <ListItemText primary={`View ${file?.path}`} />
                    </ListItemButton>
                  );
                })}
              </List>
              <Button style={{ align: 'right' }} variant="contained" onClick={() => setIsAddNewFilesMode(true)}>
                Select New Files
              </Button>
            </Stack>
          )}
        </Box>
      )}
    </>
  );
};

CustomFileDropzone.propTypes = {
  invoiceId: PropTypes.string,
  customInvoiceFiles: PropTypes.arrayOf(PropTypes.shape({ path: PropTypes.string })),
  handleChangeCustomFiles: PropTypes.func,
};

export default CustomFileDropzone;
